/**
 * ImageReloader
 * 图片轮询重载工具类（v1.4 OSS 自动解冻版）
 *
 * 设计原则：
 * - 废弃 image_restore 接口调用，改为直接轮询重载图片。
 * - 使用原 URL（无需 ?_t 参数），因 CDN 不缓存 403，OSS 解冻成功后 CDN 自然回源。
 * - 提供 Promise API，支持批量轮询和进度回调。
 * - 超时后跳过该图（等同于成功，继续后续流程），并通过 onAttempt 的 timedOut 标记上报。
 */
class ImageReloader {
    constructor(options = {}) {
        this.pollInterval = options.pollInterval || 10000;      // 轮询间隔 10s
        this.maxPollAttempts = options.maxPollAttempts || 30;   // 最大 30 次（5 分钟）
        this.onProgress = options.onProgress || (() => {});
        this.pollingTasks = new Map();
    }

    /**
     * 批量轮询重载
     * @param {Array} errorImages - 加载失败的图片列表，每项包含 { image, imageObject, url, index }
     * @param {Object} options - { onItemSuccess(item, imageObject), onItemError(item), onAttempt(item, attempts, isSuccess, timedOut) }
     * @returns {Promise<Array<{index, url, status, checkedAt}>>} - 全部成功或超时后 resolve，返回预热结果数组
     */
    async reloadBatch(errorImages, options = {}) {
        const { onItemSuccess, onItemError, onAttempt } = options;

        const reloadPromises = errorImages.map(async (item) => {
            const result = await this.reloadWithPolling(item, {
                onSuccess: onItemSuccess,
                onError: onItemError,
                onAttempt: onAttempt
            });
            const checkedAt = Date.now();
            return {
                index: typeof item.index === 'number' ? item.index : -1,
                url: item.url,
                status: result.success ? 200 : (result.timedOut ? 408 : 403),
                checkedAt
            };
        });

        return Promise.all(reloadPromises);
    }

    /**
     * 单张图片轮询重载
     * @param {Object} item - { image, imageObject, url }
     * @param {Object} options - { onSuccess(item, imageObject), onError(item), onAttempt(item, attempts, isSuccess, timedOut) }
     * @returns {Promise<{success: boolean, timedOut: boolean}>}
     */
    reloadWithPolling(item, options = {}) {
        const { onSuccess, onError, onAttempt } = options;

        return new Promise((resolve) => {
            let attempts = 0;

            const tryLoad = () => {
                attempts++;

                const im = new Image();

                im.onload = () => {
                    // 加载成功：立即写入 orisize，触发 onUpdate，清理轮询
                    onAttempt && onAttempt(item, attempts, true, false);
                    item.image['orisize'] = [im.width, im.height];
                    this.clearPolling(item.url);
                    onSuccess && onSuccess(item, im);
                    resolve({ success: true, timedOut: false });
                };

                im.onerror = () => {
                    if (attempts >= this.maxPollAttempts) {
                        // 超时：跳过该图，等同于成功继续后续流程
                        onAttempt && onAttempt(item, attempts, false, true);
                        item.image['orisize'] = [300, 300];
                        this.clearPolling(item.url);
                        onError && onError(item);
                        resolve({ success: false, timedOut: true });
                        return;
                    }

                    onAttempt && onAttempt(item, attempts, false, false);
                    // 继续轮询
                    const timer = setTimeout(tryLoad, this.pollInterval);
                    this.pollingTasks.set(item.url, { timer, attempts });
                };

                // 使用原 URL，无需 ?_t
                im.src = item.url;
            };

            tryLoad();
        });
    }

    clearPolling(url) {
        const task = this.pollingTasks.get(url);
        if (task && task.timer) clearTimeout(task.timer);
        this.pollingTasks.delete(url);
    }

    destroy() {
        this.pollingTasks.forEach((task) => {
            if (task.timer) clearTimeout(task.timer);
        });
        this.pollingTasks.clear();
    }
}

export default ImageReloader;
